import React, { useCallback, useEffect, useState } from 'react';

import i18n from '../../i18n';
// import PropTypes from 'prop-types';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import styles from './EventSubmissionFormV2.module.scss';
import { ErrorMessageSchemas, startValues } from './formikConfiguration';

import AboutStep from './AboutStep';
import ContactStep from './ContactStep';
import DateStep from './DateStep';
import ImageStep from './ImageStep';
import PlaceStep from './PlaceStep';
import SuccessStep from './SuccessStep';
import SummationStep from './SummationStep';
import TypeStep from './TypeStep';

import * as Sentry from '@sentry/nextjs';
import classNames from 'classnames';
import { camelToSnake, keysToSnakeFromCamel } from '../../utils/caseconverters';
import Button from '../Button';
import buttonStyles from '../Button/Button.module.scss';
import InputGroup from '../InputGroup';
import inputGroupStyles from '../InputGroup/InputGroup.module.scss';
import v2Styles from './EventSubmissionFormV2.module.scss';
import useLocalStorage from './useLocalStorage';

export const InputGroupV2 = (props) => {
    const modifiers = [inputGroupStyles['InputGroup--V2']];
    return <InputGroup {...props} modifiers={modifiers} />;
};

export const PrevButton = ({ onClick, disabled = false }) => {
    return (
        <Button
            type="secondary"
            text={i18n.t('EventSubmissionFormV2.buttons.prev')}
            icon="arrowLeft"
            iconDirection="left"
            iconColor="black800"
            onClick={onClick}
            disabled={disabled}
        />
    );
};

export const Submit = ({ text = '', icon = true, onClick = () => {} }) => {
    return (
        <Button
            type="primary"
            submit
            text={text ? text : i18n.t('EventSubmissionFormV2.buttons.next')}
            icon={icon ? 'arrow' : ''}
            modifiers={[buttonStyles['Button--PreventNoIconStyle']]}
            onClick={onClick}
        />
    );
};

export const PrevAndSubmitButtons = ({ onClickPrev, onlySubmit = false }) => {
    return (
        <div
            className={classNames(
                v2Styles['EventSubmissionFormV2__ButtonsWrapper'],
                onlySubmit &&
                    v2Styles['EventSubmissionFormV2__ButtonsWrapper--FlexEnd']
            )}>
            {!onlySubmit && <PrevButton onClick={onClickPrev} />}
            <Submit />
        </div>
    );
};

const SectionHeader = ({ header, text, step, richText = false }) => {
    return (
        <>
            {!!step && (
                <span className={styles['EventSubmissionFormV2__Step']}>
                    {i18n.t('EventSubmissionFormV2.step')} {step}/8
                </span>
            )}
            <h2 className={styles['EventSubmissionFormV2__StepHeader']}>
                {header}
            </h2>
            {!!richText ? (
                <div
                    className={
                        styles['EventSubmissionFormV2__StepText'] +
                        ' ' +
                        styles['EventSubmissionFormV2__StepText--RichText']
                    }
                    dangerouslySetInnerHTML={{ __html: text }}></div>
            ) : (
                <p className={styles['EventSubmissionFormV2__StepText']}>
                    {text}
                </p>
            )}
        </>
    );
};

const ContactUsBox = ({ title, text }) => {
    return (
        <aside className={styles['EventSubmissionFormV2__ContactUs']}>
            <h2 className={styles['EventSubmissionFormV2__ContactUsTitle']}>
                {title}
            </h2>
            <div
                className={styles['EventSubmissionFormV2__ContactUsText']}
                dangerouslySetInnerHTML={{ __html: text }}></div>
        </aside>
    );
};

export const RecaptchaDisclaimer = () => (
    <small className={v2Styles['EventSubmissionFormV2__RecaptchaDisclaimer']}>
        {i18n.t(`Recaptcha.protectedBy`)}
        <a href={'https://policies.google.com/privacy'} target={'_blank'}>
            {i18n.t(`Recaptcha.privacyPolicy`)}
        </a>
        {i18n.t(`Recaptcha.and`)}
        <a href={'https://policies.google.com/terms'} target={'_blank'}>
            {i18n.t(`Recaptcha.termsOfService`)}
        </a>
        {i18n.t(`Recaptcha.apply`)}
    </small>
);

const EventSubmissionFormV2 = ({
    categories,
    texts,
    contactUsBox,
    csrf,
    venueOptions,
}) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [localStorageData, setLocalStorageData, clearData] = useLocalStorage(
        'eventData',
        {}
    );
    const [initialValues, setInitialValues] = useState(startValues);
    const [formValues, setFormValues] = useState({});
    const [selectedImage, setSelectedImage] = useState({});
    const [fadeOutStep, setFadeOutStep] = useState(null);
    const [fadeInStep, setFadeInStep] = useState(null);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const { executeRecaptcha } = useGoogleReCaptcha();

    const getRecaptchaToken = useCallback(async () => {
        if (!executeRecaptcha) {
            console.log('Execute recaptcha not yet available');
            return;
        }

        return await executeRecaptcha();
    }, [executeRecaptcha]);

    useEffect(() => {
        updateInitialValues();
    }, [localStorageData]);

    useEffect(() => {
        setTimeout(() => {
            setFadeInStep(currentStep);
        }, 50);

        return () => setFadeInStep(null);
    }, [currentStep]);

    const updateInitialValues = () => {
        let updatedInitialValues = { ...initialValues };

        for (const key in localStorageData) {
            if (localStorageData.hasOwnProperty(key)) {
                const stepContainingKey = findStepForKey(initialValues, key);

                if (stepContainingKey !== null) {
                    // Update the corresponding key in initialValues with the value from localStorage
                    updatedInitialValues[stepContainingKey][key] =
                        localStorageData[key];
                }
            }
        }
        setInitialValues(updatedInitialValues);
    };

    const findStepForKey = (obj, targetKey) => {
        for (const step in obj) {
            if (obj.hasOwnProperty(step)) {
                const stepData = obj[step];
                if (stepData.hasOwnProperty(targetKey)) {
                    return step; // Return the step where the target key is found
                }
            }
        }
        return null; // Return null if the key is not found in any step
    };

    const submitForm = async (props) => {
        setErrorMessage('');
        const token = await getRecaptchaToken();

        const values = formValues;

        try {
            let imageFile = '';
            let imageTitle = '';
            let imageCredit = '';

            if (!!values.image) {
                imageFile = values.file;
                imageTitle = values.file.name;
                imageCredit = values.photoCredit;
            }

            let formData = new FormData();

            // // Recaptcha token to be verified
            formData.append('recaptchaToken', token);

            // Image to be uploaded
            formData.append('imageFile', imageFile);
            formData.append('imageTitle', imageTitle);
            formData.append('imageCredit', imageCredit);

            // Event data
            formData.append('titleEn', values.titleEn);
            formData.append('titleSv', values.titleSv);
            formData.append('descriptionEn', values.descriptionEn);
            formData.append('descriptionSv', values.descriptionSv);
            formData.append('externalWebsiteUrl', values.externalWebsiteUrl);
            formData.append('address', values.address);
            formData.append('venueName', values.venueName);
            formData.append('zipCode', values.zip);
            formData.append('submittedByEmail', values.submittedByEmail);
            formData.append('isFree', values.isFree);
            formData.append('isDigital', values.isDigital);
            formData.append('city', values.city);
            formData.append('closestStation', values.closestStation);
            formData.append('subcategory', values.subcategory);

            if (!values.mainCategory && values.categories.length > 0) {
                formData.append('mainCategory', values.categories[0]);
            } else if (values.mainCategory) {
                formData.append('mainCategory', values.mainCategory);
            }

            formData.append(
                'categories',
                JSON.stringify(values.categories.map(Number))
            );
            formData.append(
                'dateAdmin',
                JSON.stringify(
                    keysToSnakeFromCamel(values.dateAdmin).map((x) => ({
                        ...x,
                        type: camelToSnake(x.type),
                    }))
                )
            );

            const response = await fetch('/api/v1/eventsubmission/', {
                method: 'post',
                headers: {
                    Accept: 'application/json',
                    'X-CSRFToken': csrf,
                },
                body: formData,
            });

            console.log(response);

            if (response.status !== 201) {
                const errorMessage = await response.text();
                throw `Failed to create resource. Status: ${response.status}, Message: ${errorMessage}`;
            }

            setCurrentStep(currentStep + 1);
            clearData();
            scrollToTop();
        } catch (error) {
            console.error(error);
            setShowError(true);
            setErrorMessage(error);
            Sentry.withScope((scope) => {
                scope.setTags({
                    'failed-event-submission':
                        'submitForm in EventSubmissionFormV2',
                });
                Sentry.captureException(error);
            });
        }
    };

    const onClickPrev = () => {
        updateInitialValues();
        changeToStep(currentStep - 1);
    };

    const nextStep = (props) => {
        setFormValues({ ...formValues, ...props });
        setLocalStorageData({ ...localStorageData, ...props });
        updateInitialValues();
        changeToStep(currentStep + 1);
        console.log(formValues);
    };

    const nextStepForImageStep = (props) => {
        if (!!props.image && !!props.file) {
            nextStep(props);
            return;
        }

        // Props lacks image and file when changing step if a new image hasn't been selected,
        // even though an image has been set in a prior situation.
        // Consequently, the props need to be updated with the earlier image and file in selectedImage.
        nextStep({
            ...props,
            image: selectedImage.image,
            file: selectedImage.file,
        });
    };

    const changeToStep = (step) => {
        setFadeOutStep(currentStep);
        scrollToTop();
        // Delay setting the current step
        // so that the previous step will have finished fading out.
        setTimeout(() => {
            setCurrentStep(step);
        }, 500);
    };

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    const resetForm = () => {
        clearData();
        window.location.reload();
    };

    const progressLabels = [
        {
            label: i18n.t('EventSubmissionFormV2.progressBar.labels.about'),
            step: 1,
        },
        {
            label: i18n.t('EventSubmissionFormV2.progressBar.labels.type'),
            step: 2,
        },
        {
            label: i18n.t('EventSubmissionFormV2.progressBar.labels.place'),
            step: 3,
        },
        {
            label: i18n.t('EventSubmissionFormV2.progressBar.labels.date'),
            step: 4,
        },
        {
            label: i18n.t('EventSubmissionFormV2.progressBar.labels.image'),
            step: 5,
        },
        {
            label: i18n.t('EventSubmissionFormV2.progressBar.labels.contact'),
            step: 6,
        },
        {
            label: i18n.t('EventSubmissionFormV2.progressBar.labels.summary'),
            step: 7,
        },
        {
            label: i18n.t('EventSubmissionFormV2.progressBar.labels.submit'),
            step: 8,
        },
    ];

    const width = 100 / progressLabels.length / 2;
    const currentWidth = {
        1: `${width}%`,
        2: `${width * 3}%`,
        3: `${width * 5}%`,
        4: `${width * 7}%`,
        5: `${width * 9}%`,
        6: `${width * 11}%`,
        7: `${width * 13}%`,
        8: '100vw',
    };

    return (
        <div className={styles['EventSubmissionFormV2']}>
            <div
                className={
                    styles['EventSubmissionFormV2__ProgressBarBackground']
                }></div>
            <div
                className={styles['EventSubmissionFormV2__ProgressLine']}
                style={{ width: `${currentWidth[currentStep]}` }}></div>
            <div className={styles['EventSubmissionFormV2__ProgressBar']}>
                {progressLabels.map(({ label, step }) => (
                    <div
                        key={step}
                        className={classNames(
                            styles['EventSubmissionFormV2__ProgressLabel'],
                            {
                                [styles[
                                    'EventSubmissionFormV2__ProgressLabel--Active'
                                ]]: currentStep === step,
                            }
                        )}>
                        {label}
                    </div>
                ))}
            </div>
            <div className={styles['EventSubmissionFormV2__Container']}>
                <div className={styles['EventSubmissionFormV2__FormContainer']}>
                    {currentStep === 1 && (
                        <div
                            className={classNames(
                                styles['EventSubmissionFormV2__StepContainer'],
                                {
                                    [styles[
                                        'EventSubmissionFormV2__StepContainer--Show'
                                    ]]: fadeInStep === 1,
                                    [styles[
                                        'EventSubmissionFormV2__StepContainer--Hide'
                                    ]]: fadeOutStep === 1,
                                }
                            )}>
                            <h1
                                className={
                                    styles['EventSubmissionFormV2__Title']
                                }>
                                {texts.title}
                            </h1>
                            <div
                                className={
                                    styles['EventSubmissionFormV2__Preamble']
                                }
                                dangerouslySetInnerHTML={{
                                    __html: texts.preamble,
                                }}></div>
                            <SectionHeader
                                header={texts.aboutTitle}
                                text={texts.aboutText}
                                step={currentStep}
                            />
                            <AboutStep
                                initialValues={
                                    initialValues[`step${currentStep}`]
                                }
                                validationSchema={
                                    ErrorMessageSchemas[`step${currentStep}`]
                                }
                                onSubmit={nextStep}
                            />
                        </div>
                    )}
                    {currentStep === 2 && (
                        <>
                            <div
                                className={classNames(
                                    styles[
                                        'EventSubmissionFormV2__StepContainer'
                                    ],
                                    {
                                        [styles[
                                            'EventSubmissionFormV2__StepContainer--Show'
                                        ]]: fadeInStep === 2,
                                        [styles[
                                            'EventSubmissionFormV2__StepContainer--Hide'
                                        ]]: fadeOutStep === 2,
                                    }
                                )}>
                                <SectionHeader
                                    header={texts.typeTitle}
                                    text={texts.typeText}
                                    step={currentStep}
                                />

                                <TypeStep
                                    initialValues={
                                        initialValues[`step${currentStep}`]
                                    }
                                    validationSchema={
                                        ErrorMessageSchemas[
                                            `step${currentStep}`
                                        ]
                                    }
                                    categories={categories}
                                    onClickPrev={onClickPrev}
                                    onSubmit={nextStep}
                                />
                            </div>
                        </>
                    )}
                    {currentStep === 3 && (
                        <>
                            <div
                                className={classNames(
                                    styles[
                                        'EventSubmissionFormV2__StepContainer'
                                    ],
                                    {
                                        [styles[
                                            'EventSubmissionFormV2__StepContainer--Show'
                                        ]]: fadeInStep === 3,
                                        [styles[
                                            'EventSubmissionFormV2__StepContainer--Hide'
                                        ]]: fadeOutStep === 3,
                                    }
                                )}>
                                <SectionHeader
                                    header={texts.placeTitle}
                                    text={texts.placeText}
                                    step={currentStep}
                                />
                                <PlaceStep
                                    initialValues={
                                        initialValues[`step${currentStep}`]
                                    }
                                    validationSchema={
                                        ErrorMessageSchemas[
                                            `step${currentStep}`
                                        ]
                                    }
                                    venueOptions={venueOptions}
                                    onClickPrev={onClickPrev}
                                    onSubmit={nextStep}
                                />
                            </div>
                        </>
                    )}
                    {currentStep === 4 && (
                        <>
                            <div
                                className={classNames(
                                    styles[
                                        'EventSubmissionFormV2__StepContainer'
                                    ],
                                    {
                                        [styles[
                                            'EventSubmissionFormV2__StepContainer--Show'
                                        ]]: fadeInStep === 4,
                                        [styles[
                                            'EventSubmissionFormV2__StepContainer--Hide'
                                        ]]: fadeOutStep === 4,
                                    }
                                )}>
                                <SectionHeader
                                    header={texts.dateTitle}
                                    text={texts.dateText}
                                    step={currentStep}
                                />
                                <DateStep
                                    initialValues={
                                        initialValues[`step${currentStep}`]
                                    }
                                    validationSchema={
                                        ErrorMessageSchemas[
                                            `step${currentStep}`
                                        ]
                                    }
                                    onClickPrev={onClickPrev}
                                    onSubmit={nextStep}
                                />
                            </div>
                        </>
                    )}
                    {currentStep === 5 && (
                        <>
                            <div
                                className={classNames(
                                    styles[
                                        'EventSubmissionFormV2__StepContainer'
                                    ],
                                    {
                                        [styles[
                                            'EventSubmissionFormV2__StepContainer--Show'
                                        ]]: fadeInStep === 5,
                                        [styles[
                                            'EventSubmissionFormV2__StepContainer--Hide'
                                        ]]: fadeOutStep === 5,
                                    }
                                )}>
                                <SectionHeader
                                    header={texts.imageTitle}
                                    text={texts.imageText}
                                    step={currentStep}
                                />
                                <ImageStep
                                    // image and file must initially be empty, since it's impossible to pre-populate an input type="file" field out of security reasons.
                                    initialValues={{
                                        image: '',
                                        file: '',
                                        selectedImage:
                                            initialValues.step5.selectedImage,
                                        photoCredit:
                                            initialValues.step5.photoCredit,
                                        photoUploadAllowed:
                                            initialValues.step5
                                                .photoUploadAllowed,
                                    }}
                                    validationSchema={
                                        ErrorMessageSchemas[
                                            `step${currentStep}`
                                        ]
                                    }
                                    selectedImage={selectedImage}
                                    setSelectedImage={setSelectedImage}
                                    onClickPrev={onClickPrev}
                                    onSubmit={nextStepForImageStep}
                                />
                            </div>
                        </>
                    )}
                    {currentStep === 6 && (
                        <>
                            <div
                                className={classNames(
                                    styles[
                                        'EventSubmissionFormV2__StepContainer'
                                    ],
                                    {
                                        [styles[
                                            'EventSubmissionFormV2__StepContainer--Show'
                                        ]]: fadeInStep === 6,
                                        [styles[
                                            'EventSubmissionFormV2__StepContainer--Hide'
                                        ]]: fadeOutStep === 6,
                                    }
                                )}>
                                <SectionHeader
                                    header={texts.contactTitle}
                                    text={texts.contactText}
                                    step={currentStep}
                                />
                                <ContactStep
                                    initialValues={
                                        initialValues[`step${currentStep}`]
                                    }
                                    validationSchema={
                                        ErrorMessageSchemas[
                                            `step${currentStep}`
                                        ]
                                    }
                                    consentText={texts.contactConsent}
                                    onClickPrev={onClickPrev}
                                    onSubmit={nextStep}
                                />
                            </div>
                        </>
                    )}
                    {currentStep === 7 && (
                        <>
                            <div
                                className={classNames(
                                    styles[
                                        'EventSubmissionFormV2__StepContainer'
                                    ],
                                    {
                                        [styles[
                                            'EventSubmissionFormV2__StepContainer--Show'
                                        ]]: fadeInStep === 7,
                                        [styles[
                                            'EventSubmissionFormV2__StepContainer--Hide'
                                        ]]: fadeOutStep === 7,
                                    }
                                )}>
                                <SectionHeader
                                    header={texts.summationTitle}
                                    text={texts.summationText}
                                    richText
                                    step={currentStep}
                                />
                                <SummationStep
                                    initialValues={
                                        initialValues[`step${currentStep}`]
                                    }
                                    validationSchema={
                                        ErrorMessageSchemas[
                                            `step${currentStep}`
                                        ]
                                    }
                                    categories={categories}
                                    formValues={formValues}
                                    onClickPrev={onClickPrev}
                                    onSubmit={submitForm}
                                    showError={showError}
                                    errorMessage={errorMessage}
                                />
                            </div>
                        </>
                    )}
                    {currentStep === 8 && (
                        <>
                            <div
                                className={classNames(
                                    styles[
                                        'EventSubmissionFormV2__StepContainer'
                                    ],
                                    {
                                        [styles[
                                            'EventSubmissionFormV2__StepContainer--Show'
                                        ]]: fadeInStep === 8,
                                        [styles[
                                            'EventSubmissionFormV2__StepContainer--Hide'
                                        ]]: fadeOutStep === 8,
                                    }
                                )}>
                                <SectionHeader
                                    header={texts.successTitle}
                                    text={texts.successText}
                                    richText
                                />
                                <SuccessStep
                                    formValues={formValues}
                                    categories={categories}
                                    onSubmit={resetForm}
                                />
                            </div>
                        </>
                    )}
                </div>
                <ContactUsBox {...contactUsBox} />
            </div>
            <div className={styles['EventSubmissionFormV2__BottomProgressBar']}>
                <div
                    className={
                        styles['EventSubmissionFormV2__ProgressBarBackground']
                    }></div>
                <div
                    className={styles['EventSubmissionFormV2__ProgressLine']}
                    style={{ width: `${currentWidth[currentStep]}` }}></div>
            </div>
        </div>
    );
};

EventSubmissionFormV2.propTypes = {};

export default EventSubmissionFormV2;
